import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart3, Crown, Share2, HeartPulse, BookOpenText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/auth";
import { useLanguage } from "@/contexts/language";
import { Language } from "@/contexts/language";
import { t } from "@/utils/translations";

const PremiumFeatures = () => {
  const navigate = useNavigate();
  const { isPremium } = useAuth(); // Now correctly typed
  const { language } = useLanguage();
  
  const handleSubscribe = () => {
    navigate('/premium', { state: { action: 'subscribe' } });
  };
  
  return (
    <>
      <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
        {getPremiumFeatures(language).map((feature, index) => (
          <Card key={index} className="text-left animate-fade-up" style={{ animationDelay: `${(index + 1) * 100}ms` }}>
            <CardHeader className="pb-2">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                {feature.icon}
              </div>
              <CardTitle className="text-lg">{feature.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">{feature.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      
      <div className="mt-10 text-center">
        <Button 
          size="lg"
          onClick={handleSubscribe}
          className="px-8 animate-pulse"
          disabled={isPremium}
        >
          <Crown className="mr-2 h-4 w-4" />
          {isPremium 
            ? (language === 'ja' ? '既にサブスクリプション中' : 'Already Subscribed') 
            : t('index.premiumBenefits.upgradeToPremium', language)}
        </Button>
      </div>
    </>
  );
};

const getPremiumFeatures = (language: Language) => [
  {
    title: t('features.personalized', language),
    description: t('benefits.unlimitedDescription', language),
    icon: <BookOpenText className="h-5 w-5 text-primary" />
  },
  {
    title: t('features.statistics', language),
    description: t('benefits.growthDescription', language),
    icon: <BarChart3 className="h-5 w-5 text-primary" />
  },
  {
    title: t('common.share', language),
    description: language === 'ja' ? '安全な共有リンクを通じて大切な瞬間を家族と共有します。' : 
                language === 'es' ? 'Comparte momentos especiales con tus seres queridos a través de enlaces compartidos seguros.' :
                language === 'zh' ? '通过安全的共享链接与亲人共享特殊时刻。' :
                'Share special moments with loved ones through secure, shareable links.',
    icon: <Share2 className="h-5 w-5 text-primary" />
  },
  {
    title: t('features.healthInsights', language),
    description: language === 'ja' ? '時間の経過に伴う赤ちゃんの発達パターンに関する洞察を得ることができます。' : 
                 language === 'es' ? 'Obtén información sobre los patrones de desarrollo de tu bebé a lo largo del tiempo.' :
                 language === 'zh' ? '随着时间的推移，了解宝宝的发育模式。' :
                 'Get insights into your baby\'s development patterns over time.',
    icon: <HeartPulse className="h-5 w-5 text-primary" />
  },
  {
    title: t('features.advancedGeneration', language),
    description: language === 'ja' ? '高度なオプションを使用して、より詳細でパーソナライズされた日記エントリー。' : 
                 language === 'es' ? 'Entradas de diario más detalladas y personalizadas con opciones avanzadas.' :
                 language === 'zh' ? '使用高级选项生成更详细、更个性化的日记条目。' :
                 'More detailed and personalized diary entries with advanced options.',
    icon: <BookOpenText className="h-5 w-5 text-primary" />
  },
  {
    title: t('benefits.prioritySupport', language),
    description: t('benefits.supportDescription', language),
    icon: <Crown className="h-5 w-5 text-primary" />
  }
];

export default PremiumFeatures;
